/**
 * Signing in and accepting invites.
 *
 * Two screens over the same overlay. Which one you get is decided by the URL:
 * /invite/<token> is the signup flow, anything else is the login form. There
 * is no self-service registration — an invite is the only way in.
 */

import { PASSWORD_MIN_HINT } from '../constants.js';
import { ApiError, acceptInvite, checkInvite, login } from '../api.js';
import { $, el } from './dom.js';

export interface AuthResult {
  name: string;
}

/** Pull the token out of /invite/<token>, if that is where we are. */
export function inviteTokenFromUrl(): string | null {
  const match = location.pathname.match(/^\/invite\/([A-Za-z0-9_-]+)\/?$/);
  return match?.[1] ?? null;
}

/** Drop the token from the address bar once used, so it is not left in history. */
function cleanUrl(): void {
  if (location.pathname.startsWith('/invite/')) {
    history.replaceState(null, '', '/');
  }
}

export class AuthScreen {
  private readonly overlay = $('auth');
  private readonly title = $('auth-title');
  private readonly subtitle = $('auth-subtitle');
  private readonly form = $<HTMLFormElement>('auth-form');
  private readonly emailField = $('auth-email-field');
  private readonly emailInput = $<HTMLInputElement>('auth-email');
  private readonly nameField = $('auth-name-field');
  private readonly nameInput = $<HTMLInputElement>('auth-name');
  private readonly passwordInput = $<HTMLInputElement>('auth-password');
  private readonly passwordHint = $('auth-password-hint');
  private readonly submit = $<HTMLButtonElement>('auth-submit');
  private readonly errorNode = $('auth-error');
  private readonly noticeNode = $('auth-notice');

  private mode: 'login' | 'invite' = 'login';
  private inviteToken: string | null = null;

  constructor(private readonly onSignedIn: () => void) {
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.handleSubmit();
    });
  }

  /** Decide which screen to show. Resolves once the user is signed in. */
  async begin(): Promise<void> {
    const token = inviteTokenFromUrl();
    if (token) {
      await this.showInvite(token);
    } else {
      this.showLogin();
    }
    this.overlay.hidden = false;
  }

  private showLogin(): void {
    this.mode = 'login';
    this.title.textContent = 'Virtual Office';
    this.subtitle.textContent = 'Sign in to walk back in.';
    this.emailField.hidden = false;
    this.nameField.hidden = true;
    this.emailInput.readOnly = false;
    this.emailInput.value = '';
    this.passwordInput.autocomplete = 'current-password';
    this.passwordHint.hidden = true;
    this.submit.textContent = 'Sign in';
    this.clearMessages();
    this.emailInput.focus();
  }

  private async showInvite(token: string): Promise<void> {
    this.mode = 'invite';
    this.inviteToken = token;
    this.title.textContent = 'Join the office';
    this.subtitle.textContent = 'Checking your invite…';
    this.emailField.hidden = false;
    this.nameField.hidden = false;
    this.emailInput.readOnly = true;
    this.passwordInput.autocomplete = 'new-password';
    this.passwordHint.textContent = PASSWORD_MIN_HINT;
    this.passwordHint.hidden = false;
    this.submit.textContent = 'Create my account';
    this.submit.disabled = true;
    this.clearMessages();

    try {
      const invite = await checkInvite(token);
      this.emailInput.value = invite.email;
      this.subtitle.textContent =
        invite.role === 'admin'
          ? 'Your invite is for an admin account.'
          : 'Pick a display name and a password.';
      this.submit.disabled = false;
      this.nameInput.focus();
    } catch (error) {
      // An expired or already-used link: offer the login form as the way out.
      this.showError(error instanceof Error ? error.message : 'That invite is not valid.');
      this.subtitle.textContent = '';
      this.submit.disabled = true;
      this.offerLogin();
    }
  }

  /** After a dead invite, let them fall back to signing in. */
  private offerLogin(): void {
    const link = el('button', { className: 'linkish', text: 'Sign in instead' });
    link.type = 'button';
    link.addEventListener('click', () => {
      cleanUrl();
      this.showLogin();
    });
    this.noticeNode.replaceChildren(link);
    this.noticeNode.hidden = false;
  }

  private async handleSubmit(): Promise<void> {
    this.clearMessages();
    this.submit.disabled = true;

    try {
      if (this.mode === 'login') {
        await login(this.emailInput.value.trim(), this.passwordInput.value);
      } else {
        if (!this.inviteToken) throw new Error('Missing invite token.');
        const name = this.nameInput.value.trim();
        if (!name) throw new Error('Pick a display name.');
        await acceptInvite(this.inviteToken, name, this.passwordInput.value);
        cleanUrl();
      }
      this.passwordInput.value = '';
      this.hide();
      this.onSignedIn();
    } catch (error) {
      const message =
        error instanceof ApiError || error instanceof Error
          ? error.message
          : 'Something went wrong.';
      this.showError(message);
      this.passwordInput.select();
    } finally {
      this.submit.disabled = false;
    }
  }

  private clearMessages(): void {
    this.errorNode.hidden = true;
    this.errorNode.textContent = '';
    this.noticeNode.hidden = true;
    this.noticeNode.replaceChildren();
  }

  showError(message: string): void {
    this.errorNode.textContent = message;
    this.errorNode.hidden = false;
  }

  /** Called when the socket reports the session has gone. */
  reopen(message?: string): void {
    this.showLogin();
    if (message) this.showError(message);
    this.overlay.hidden = false;
  }

  hide(): void {
    this.overlay.hidden = true;
  }
}
